// Centralized icon components.
// Never use a word, letter, or unicode character as an icon.
// Conventions — do not invent alternatives:
//   Add / create  → CirclePlusIcon (always green)
//   Delete        → TrashIcon (always red)
//   Edit          → EditIcon (always yellow)
// SVG icons use currentColor unless noted.

export function Envelope({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      width={18}
      height={18}
      aria-hidden="true"
    >
      <rect x="1" y="3" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M1 5l7 4 7-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function StatusDot({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      className={className}
      width={16}
      height={16}
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="5" />
    </svg>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={`animate-spin-indicator ${className ?? ""}`}
      width={16}
      height={16}
      aria-hidden="true"
    >
      <circle
        cx="8"
        cy="8"
        r="5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="25.13"
        strokeDashoffset="8"
      />
    </svg>
  );
}

export function CheckCircle({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      width={16}
      height={16}
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="7" fill="currentColor" opacity="0.2" />
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path
        d="M5 8.5L7 10.5L11 5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Terminal({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      width={18}
      height={18}
      aria-hidden="true"
    >
      <rect x="1" y="2" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4.5 6L7 8.5L4.5 11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.5 11H11.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Reconnect({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 16" fill="none" className={className} width={18} height={14} aria-hidden="true">
      <path d="M12 2l4 3-4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 5H8a4 4 0 00-4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8 14l-4-3 4-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 11h8a4 4 0 004-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function ChevronDown({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className} width={12} height={12} aria-hidden="true">
      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </svg>
  );
}

export function XCircle({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      width={16}
      height={16}
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="7" fill="currentColor" opacity="0.2" />
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path
        d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function RefreshIcon({ className = "w-4 h-4" }: { className?: string }) {
  return <MaskIcon src="/icons/refresh.png" className={`bg-current ${className}`} />;
}

export function ImportIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      width={16}
      height={16}
      aria-hidden="true"
    >
      <path
        d="M8 2v7m0 0L5.5 6.5M8 9l2.5-2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 11.5V13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      width={16}
      height={16}
      aria-hidden="true"
    >
      <path
        d="M8 2.5 8.9 6.1 12.5 7 8.9 7.9 8 11.5 7.1 7.9 3.5 7 7.1 6.1 8 2.5Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path
        d="M12.5 10.5 13 12l1.5.5L13 13l-.5 1.5L12 13l-1.5-.5L12 12l.5-1.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function ChevronLeft({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      width={16}
      height={16}
      aria-hidden="true"
    >
      <path
        d="M10 3.5 5.5 8 10 12.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Download({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      width={16}
      height={16}
      aria-hidden="true"
    >
      <path
        d="M8 2v8m0 0L5 7.5M8 10l3-2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 13h10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}


/** A raster icon from public/icons tinted to a theme color via CSS mask. */
function MaskIcon({ src, className = "" }: { src: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={`inline-block align-middle ${className}`}
      style={{
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
}

/** Standard add (circle-plus) icon — always the green PNG. Use for every add/create control. */
export function CirclePlusIcon({ className = "w-4 h-4" }: { className?: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src="/icons/greenCirclePlus.png"
      alt=""
      className={`inline-block align-middle ${className}`}
    />
  );
}

/** Collapse/expand chevron (PNG mask). Point right; rotate-90 when open. */
export function ChevronIcon({ className = "w-3 h-3" }: { className?: string }) {
  return <MaskIcon src="/icons/chevron.png" className={`bg-current ${className}`} />;
}

/** Standard edit (pencil) icon — always yellow. Use for edit buttons. */
export function EditIcon({ className = "w-4 h-4" }: { className?: string }) {
  return <MaskIcon src="/icons/edit.png" className={`bg-yellow ${className}`} />;
}

/** Standard delete (trash) icon — always red. Use for delete buttons. */
export function TrashIcon({ className = "w-4 h-4" }: { className?: string }) {
  return <MaskIcon src="/icons/trash.png" className={`bg-red ${className}`} />;
}

/** Viewfinder with text lines — "OCR this". */
export function ScanTextIcon({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 8h10M7 12h10M7 16h6" />
    </svg>
  );
}
